import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReviewerLayout from '../../components/Layout/ReviewerLayout';
import DeadlineBanner from '../../components/DeadlineBanner';
import { api } from '../../services/api';
import type { UploadDownloadItem } from '../../services/api';
import type { ReviewScoreItem, ReviewCriterion, DeficiencyRequest, DeficiencyCreateRequest, DeadlinesConfig, AiSummary, AiSummaryData, ReviewerPrequalificationDetail, ComplianceCheck } from '@vedjegy/shared';

interface AppDetail {
  id: string;
  year: number;
  type: string;
  status: string;
  companyName: string;
  taxId: string;
  submittedAt: string | null;
  formData: Record<string, Record<string, string | null>>;
  prequalification: { total_score: number | null; max_score: number | null; passed: boolean | null } | null;
  excellentSuggested?: boolean;
  excellentApproved?: boolean | null;
}


export default function ReviewerApplicationPage() {
  const { id: applicationId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [app, setApp]           = useState<AppDetail | null>(null);
  const [criteria, setCriteria] = useState<ReviewCriterion[]>([]);
  const [scores, setScores]     = useState<Record<string, ReviewScoreItem>>({});
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [activeTab, setActiveTab] = useState<'review' | 'uploads' | 'deficiency' | 'ai' | 'preq' | 'compliance'>('review');
  const [saving, setSaving]     = useState<string | null>(null);
  const [finalizing, setFinalizing]     = useState(false);
  const [finalized, setFinalized]       = useState(false);
  const [unfinalizing, setUnfinalizing] = useState(false);
  const [draft, setDraft]       = useState<Record<string, { score: string; comment: string }>>({});
  const saveTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const [uploads, setUploads]             = useState<UploadDownloadItem[]>([]);
  const [uploadsLoaded, setUploadsLoaded] = useState(false);
  const [zipDownloading, setZipDownloading] = useState(false);
  const [zipError, setZipError]             = useState('');
  const [excellentSaving, setExcellentSaving] = useState(false);
  const [excellentError, setExcellentError]   = useState('');

  const [compliance, setCompliance]                   = useState<ComplianceCheck | null>(null);
  const [complianceLoaded, setComplianceLoaded]       = useState(false);
  const [complianceRechecking, setComplianceRechecking] = useState(false);
  const [complianceError, setComplianceError]         = useState('');

  // Conflict of interest state
  const [conflictModal, setConflictModal]     = useState(false);
  const [conflictSending, setConflictSending] = useState(false);
  const [conflictError, setConflictError]     = useState('');

  // Deficiency state
  const [deficiencies, setDeficiencies]             = useState<DeficiencyRequest[]>([]);
  const [deficiencyDesc, setDeficiencyDesc]         = useState('');
  const [deficiencyDeadline, setDeficiencyDeadline] = useState('');
  const [deficiencySubmitting, setDeficiencySubmitting] = useState(false);
  const [deficiencyError, setDeficiencyError]       = useState('');
  // Per-section notes the reviewer fills in on the Bírálat tab before sending a deficiency request
  const [sectionNotes, setSectionNotes] = useState<Record<string, string>>({});

  const [deadlines, setDeadlines] = useState<DeadlinesConfig | null>(null);

  // Reviewer note
  const [note, setNote]                   = useState('');
  const [noteSaved, setNoteSaved]         = useState(false);
  const noteSaveTimer                     = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Prequalification detail state (lazy-loaded when the tab is first opened)
  const [preqData, setPreqData]           = useState<ReviewerPrequalificationDetail | null>(null);
  const [preqLoaded, setPreqLoaded]       = useState(false);
  const [preqError, setPreqError]         = useState('');

  // AI summary state
  const [aiSummary, setAiSummary]         = useState<AiSummary | null>(null);
  const [aiLoaded, setAiLoaded]           = useState(false);
  const [aiGenerating, setAiGenerating]   = useState(false);
  const [aiAccepting, setAiAccepting]     = useState<string | null>(null);
  const [aiError, setAiError]             = useState('');
  const [aiExpanded, setAiExpanded]       = useState<Set<string>>(new Set());
  const [saveError, setSaveError]         = useState('');
  const [finalizeError, setFinalizeError]     = useState('');
  const [unfinalizeError, setUnfinalizeError] = useState('');

  async function handleExcellentDecision(approved: boolean | null) {
    if (!applicationId) return;
    setExcellentSaving(true);
    setExcellentError('');
    try {
      const res = await api.reviewer.setExcellentApproved(applicationId, approved);
      setApp((prev) => prev ? { ...prev, excellentApproved: res.excellentApproved } : prev);
    } catch {
      setExcellentError('Döntés mentése sikertelen.');
    } finally {
      setExcellentSaving(false);
    }
  }

  const load = useCallback(async () => {
    if (!applicationId) return;
    setLoading(true);
    setError('');
    try {
      // Load the application first to know its year, then load criteria for that year
      // alongside the other parallel requests.
      const appRes = await api.reviewer.getApplication(applicationId);
      const [scoresRes, uploadsRes, criteriaRes, deficiencyRes, deadlineRes, noteRes] = await Promise.all([
        api.reviewer.getScores(applicationId),
        api.reviewer.getUploads(applicationId),
        api.reviewer.getCriteria(appRes.year + 1),
        api.reviewer.getDeficiency(applicationId),
        api.config.getDeadlines().catch(() => null),
        api.reviewer.getNote(applicationId).catch(() => ({ note: null })),
      ]);
      setApp(appRes);
      setCriteria(criteriaRes.criteria);
      const scoresMap: Record<string, ReviewScoreItem> = {};
      const draftMap: Record<string, { score: string; comment: string }> = {};
      for (const s of scoresRes.scores) {
        scoresMap[s.criterionId] = s;
        draftMap[s.criterionId] = { score: String(s.score ?? ''), comment: s.comment ?? '' };
      }
      setScores(scoresMap);
      setDraft(draftMap);
      setFinalized(scoresRes.scores.some((s) => s.isFinal));
      setUploads(uploadsRes.uploads);
      setUploadsLoaded(true);
      setDeficiencies(deficiencyRes.deficiencies);
      setDeadlines(deadlineRes);
      setNote(noteRes.note ?? '');
      // Pre-fill section notes from the latest deficiency so the reviewer can see what was sent
      const latestNotes = deficiencyRes.deficiencies[0]?.sectionNotes;
      if (latestNotes) setSectionNotes(latestNotes);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Betöltési hiba.');
    } finally {
      setLoading(false);
    }
  }, [applicationId]);

  useEffect(() => { void load(); }, [load]);

  // Load existing AI summary when the AI tab is first opened
  useEffect(() => {
    if (activeTab !== 'preq' || preqLoaded || !applicationId) return;
    setPreqLoaded(true);
    api.reviewer.getPrequalification(applicationId)
      .then((data) => setPreqData(data))
      .catch((e: unknown) => setPreqError(e instanceof Error ? e.message : 'Hiba az előminősítés betöltésekor.'));
  }, [activeTab, preqLoaded, applicationId]);

  useEffect(() => {
    if (activeTab !== 'ai' || aiLoaded || !applicationId) return;
    setAiLoaded(true);
    api.ai.getSummary(applicationId)
      .then((res) => setAiSummary(res.summary))
      .catch(() => { /* no summary yet */ });
  }, [activeTab, aiLoaded, applicationId]);

  useEffect(() => {
    if (activeTab !== 'compliance' || complianceLoaded || !applicationId) return;
    setComplianceLoaded(true);
    api.compliance.getForReviewer(applicationId)
      .then((res) => setCompliance(res.check))
      .catch(() => { /* no check yet */ });
  }, [activeTab, complianceLoaded, applicationId]);

  async function handleComplianceRecheck() {
    if (!applicationId) return;
    setComplianceRechecking(true);
    setComplianceError('');
    try {
      await api.compliance.recheckForReviewer(applicationId);
      // Poll until new result appears
      const poll = setInterval(() => {
        api.compliance.getForReviewer(applicationId)
          .then((res) => {
            if (res.check && res.check.id !== compliance?.id) {
              setCompliance(res.check);
              clearInterval(poll);
              setComplianceRechecking(false);
            }
          })
          .catch(() => { /* silent */ });
      }, 3000);
      setTimeout(() => { clearInterval(poll); setComplianceRechecking(false); }, 90_000);
    } catch {
      setComplianceError('Nem sikerült elindítani az ellenőrzést.');
      setComplianceRechecking(false);
    }
  }

  // Poll every 2s while a summary is pending or running
  useEffect(() => {
    if (!applicationId) return;
    if (!aiSummary || !['pending', 'running'].includes(aiSummary.status)) return;
    const t = setInterval(() => {
      api.ai.getSummary(applicationId)
        .then((res) => setAiSummary(res.summary))
        .catch(() => { /* silent */ });
    }, 2000);
    return () => clearInterval(t);
  }, [applicationId, aiSummary?.status]);

  async function generateAiSummary() {
    if (!applicationId) return;
    setAiGenerating(true);
    setAiError('');
    try {
      await api.ai.triggerSummary(applicationId);
      const res = await api.ai.getSummary(applicationId);
      setAiSummary(res.summary);
    } catch (e) {
      setAiError(e instanceof Error ? e.message : 'Hiba az AI elemzés indítása közben.');
    } finally {
      setAiGenerating(false);
    }
  }

  async function acceptAiScores(sectionId: string) {
    if (!aiSummary || !applicationId) return;
    setAiAccepting(sectionId);
    setAiError('');
    try {
      await api.ai.acceptScores(applicationId, { sectionId, summaryId: aiSummary.id });
      const scoresRes = await api.reviewer.getScores(applicationId);
      const map: Record<string, ReviewScoreItem> = {};
      for (const s of scoresRes.scores) map[s.criterionId] = s;
      setScores(map);
    } catch (e) {
      setAiError(e instanceof Error ? e.message : 'Hiba a pontok elfogadásakor.');
    } finally {
      setAiAccepting(null);
    }
  }

  function toggleAiSection(sectionId: string) {
    setAiExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) next.delete(sectionId);
      else next.add(sectionId);
      return next;
    });
  }

  // Schedule an autosave 800ms after the last change for a given criterion/sub-criterion.
  // scoreVal and maxScore are passed explicitly to avoid stale closures.
  function scheduleSave(criterionId: string, scoreVal: number, maxScore: number, comment?: string) {
    if (saveTimers.current[criterionId]) clearTimeout(saveTimers.current[criterionId]);
    saveTimers.current[criterionId] = setTimeout(() => void saveScore(criterionId, scoreVal, maxScore, comment), 800);
  }

  async function saveScore(criterionId: string, scoreVal: number, maxScore: number, comment?: string) {
    if (!applicationId) return;
    if (isNaN(scoreVal) || scoreVal < 0 || scoreVal > maxScore) return;

    setSaving(criterionId);
    setSaveError('');
    try {
      await api.reviewer.upsertScore(applicationId, criterionId, {
        score: scoreVal,
        maxScore,
        comment: comment || undefined,
      });
      setScores((prev) => ({
        ...prev,
        [criterionId]: {
          criterionId,
          score: scoreVal,
          maxScore,
          comment: comment || null,
          isFinal: false,
          updatedAt: new Date().toISOString(),
        },
      }));
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Mentési hiba.');
    } finally {
      setSaving(null);
    }
  }

  async function handleFinalize() {
    if (!applicationId) return;
    if (!confirm('Biztosan véglegesíti a pontozást? Utána nem módosítható.')) return;
    setFinalizing(true);
    setFinalizeError('');
    try {
      const res = await api.reviewer.finalizeScores(applicationId);
      setFinalized(true);
      // Server recomputed the excellent nomination — reflect it without a reload.
      if (res.excellentSuggested !== null && res.excellentSuggested !== undefined) {
        setApp((prev) => prev ? { ...prev, excellentSuggested: res.excellentSuggested ?? false, excellentApproved: null } : prev);
      }
      setScores((prev) => {
        const updated = { ...prev };
        for (const k of Object.keys(updated)) {
          updated[k] = { ...updated[k], isFinal: true };
        }
        return updated;
      });
    } catch (e) {
      setFinalizeError(e instanceof Error ? e.message : 'Véglegesítési hiba.');
    } finally {
      setFinalizing(false);
    }
  }

  async function handleUnfinalize() {
    if (!applicationId) return;
    if (!confirm('Visszanyitja a bírálatot szerkesztésre? A véglegesítés visszavonható.')) return;
    setUnfinalizing(true);
    setUnfinalizeError('');
    try {
      await api.reviewer.unfinalizeScores(applicationId);
      setFinalized(false);
      // Reopening clears the excellent nomination and any board decision (recomputed on next finalize).
      setApp((prev) => prev ? { ...prev, excellentSuggested: false, excellentApproved: null } : prev);
      setScores((prev) => {
        const updated = { ...prev };
        for (const k of Object.keys(updated)) {
          updated[k] = { ...updated[k], isFinal: false };
        }
        return updated;
      });
    } catch (e) {
      setUnfinalizeError(e instanceof Error ? e.message : 'Visszanyitási hiba.');
    } finally {
      setUnfinalizing(false);
    }
  }

  function handleNoteChange(value: string) {
    setNote(value);
    setNoteSaved(false);
    if (noteSaveTimer.current) clearTimeout(noteSaveTimer.current);
    noteSaveTimer.current = setTimeout(() => {
      if (!applicationId) return;
      api.reviewer.updateNote(applicationId, value || null)
        .then(() => setNoteSaved(true))
        .catch(() => { /* silent */ });
    }, 1000);
  }

  async function handleDownloadAttachmentsZip() {
    if (!applicationId) return;
    setZipDownloading(true);
    setZipError('');
    try {
      const res = await api.reviewer.downloadAttachmentsZip(applicationId);
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setZipError((body as { message?: string }).message ?? 'Letöltés sikertelen.');
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mellekletek_${applicationId.slice(0, 8)}.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setZipError('Letöltés sikertelen.');
    } finally {
      setZipDownloading(false);
    }
  }

  async function handleConflict() {
    if (!applicationId) return;
    setConflictSending(true);
    setConflictError('');
    try {
      await api.reviewer.reportConflict(applicationId);
      navigate('/reviewer/applications');
    } catch (e) {
      setConflictError(e instanceof Error ? e.message : 'Hiba történt.');
      setConflictSending(false);
    }
  }

  async function handleCreateDeficiency() {
    const hasNotes = Object.values(sectionNotes).some((v) => v.trim().length > 0);
    if (!applicationId || (!deficiencyDesc.trim() && !hasNotes)) return;
    setDeficiencySubmitting(true);
    setDeficiencyError('');
    try {
      const data: DeficiencyCreateRequest = { description: deficiencyDesc.trim() };
      if (deficiencyDeadline) data.deadline = deficiencyDeadline;
      // Include non-empty per-section notes
      const filteredNotes = Object.fromEntries(Object.entries(sectionNotes).filter(([, v]) => v.trim().length > 0));
      if (Object.keys(filteredNotes).length > 0) data.sectionNotes = filteredNotes;
      const created = await api.reviewer.createDeficiency(applicationId, data);
      setDeficiencies((prev) => [created, ...prev]);
      setDeficiencyDesc('');
      setDeficiencyDeadline('');
      const appRes = await api.reviewer.getApplication(applicationId);
      setApp(appRes);
    } catch (e) {
      setDeficiencyError(e instanceof Error ? e.message : 'Küldési hiba.');
    } finally {
      setDeficiencySubmitting(false);
    }
  }

  // For standard criteria, scores are stored under sub-criterion IDs (e.g., '3.1.relevance').
  // For simple criteria, scores are stored under the criterion ID directly.
  const totalScore = criteria.reduce((sum, c) => {
    if (c.rubricType === 'simple') return sum + (scores[c.criterionId]?.score ?? 0);
    return sum + c.subcriteria.reduce((s, sub) => s + (scores[`${c.criterionId}.${sub.subcritId}`]?.score ?? 0), 0);
  }, 0);
  const totalMax = criteria.reduce((sum, c) => sum + c.maxScore, 0);
  // Count criteria where at least one score has been saved
  const scoredCount = criteria.filter((c) => {
    if (c.rubricType === 'simple') return !!scores[c.criterionId];
    return c.subcriteria.some((sub) => !!scores[`${c.criterionId}.${sub.subcritId}`]);
  }).length;

  const canCreateDeficiency  = app?.status === 'under_review' || app?.status === 'deficiency_submitted';
  const pendingDeficiencies  = deficiencies.filter((d) => !d.resolvedAt);

  // Numeric sort key for section/criterion IDs like "3.1", "3.10", "intro".
  // Non-numbered sections (intro, etc.) sort before numbered ones.
  function sectionSortKey(s: string): [number, number, number] {
    const m = s.match(/^(\d+)\.(\d+)/);
    if (!m) return [0, 0, 0];
    return [1, parseInt(m[1], 10), parseInt(m[2], 10)];
  }
  function compareSections(a: string, b: string): number {
    const [a0, a1, a2] = sectionSortKey(a);
    const [b0, b1, b2] = sectionSortKey(b);
    return a0 - b0 || a1 - b1 || a2 - b2;
  }

  // Criteria sorted numerically — pairs positionally with sorted formData sections
  const sortedCriteria = [...criteria].sort((a, b) => compareSections(a.criterionId, b.criterionId));

  if (loading) return <ReviewerLayout><div style={{ padding: 40, color: 'var(--muted-foreground)' }}>Betöltés...</div></ReviewerLayout>;
  if (error || !app) return <ReviewerLayout><div style={{ padding: 40 }}><div className="alert alert-destructive"><span>!</span><div><p>{error || 'Pályázat nem található.'}</p></div></div></div></ReviewerLayout>;

  return (
    <ReviewerLayout>
      {/* Conflict of interest confirmation modal */}
      {conflictModal && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1000,
          background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            background: '#fff', borderRadius: 'var(--radius-lg)',
            padding: '32px 36px', maxWidth: 480, width: '90%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          }}>
            <h2 style={{ margin: '0 0 12px', fontSize: 18, fontWeight: 700, color: 'var(--primary)' }}>
              Összeférhetetlenség jelzése
            </h2>
            <p style={{ margin: '0 0 8px', fontSize: 14, color: '#374151', lineHeight: 1.6 }}>
              Megerősíti, hogy összeférhetetlenséget jelent a{' '}
              <strong>{app?.companyName}</strong> pályázatával kapcsolatban?
            </p>
            <p style={{ margin: '0 0 24px', fontSize: 13, color: 'var(--muted-foreground)', lineHeight: 1.6 }}>
              A jelzés után hozzárendelése törlésre kerül, a pályázat visszakerül beküldött
              státuszba, és az adminisztrátor e-mailben értesítést kap.
            </p>
            {conflictError && (
              <div style={{ marginBottom: 16, padding: '9px 14px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 6, fontSize: 13, color: '#dc2626' }}>
                {conflictError}
              </div>
            )}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setConflictModal(false); setConflictError(''); }}
                disabled={conflictSending}
                style={{ padding: '9px 20px', fontSize: 14, background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: 'var(--radius-sm)', cursor: 'pointer' }}>
                Mégsem
              </button>
              <button
                onClick={() => void handleConflict()}
                disabled={conflictSending}
                style={{ padding: '9px 20px', fontSize: 14, fontWeight: 700, background: '#d97706', color: '#fff', border: 'none', borderRadius: 'var(--radius-sm)', cursor: conflictSending ? 'default' : 'pointer', opacity: conflictSending ? 0.7 : 1 }}>
                {conflictSending ? 'Küldés…' : 'Igen, jelzem'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ padding: '32px 40px' }}>

        {/* Deadline countdown banner */}
        {deadlines && <DeadlineBanner type="review" deadlines={deadlines} />}

        {/* Header */}
        <div style={{ marginBottom: 24 }}>
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/reviewer/applications')} style={{ marginBottom: 14 }}>
            ← Vissza
          </button>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--primary)' }}>{app.companyName}</h1>
              <div style={{ fontSize: 13, color: 'var(--muted-foreground)', marginTop: 4 }}>
                {app.taxId} · {app.year} · {app.type === 'new' ? 'Új pályázat' : 'Megújítás'}
                {app.submittedAt && ' · Beküldve: ' + new Date(app.submittedAt).toLocaleDateString('hu-HU')}
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 10 }}>
              {/* Score summary */}
              <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '12px 20px', textAlign: 'center', minWidth: 120 }}>
                <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--primary)' }}>{totalScore}</div>
                <div style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>/ {totalMax} pont</div>
                <div style={{ fontSize: 11, color: 'var(--muted-foreground)', marginTop: 2 }}>{scoredCount} / {criteria.length} kritérium</div>
              </div>
              {/* Excellent applicant badge + decision buttons */}
              {app.excellentSuggested && (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
                  {app.excellentApproved === null && (
                    <span style={{ fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 12, background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a' }}>
                      ⭐ Kiváló jelölt — döntés függőben
                    </span>
                  )}
                  {app.excellentApproved === true && (
                    <span style={{ fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 12, background: '#d1fae5', color: '#065f46', border: '1px solid #6ee7b7' }}>
                      ✨ Kiváló — elfogadva
                    </span>
                  )}
                  {app.excellentApproved === false && (
                    <span style={{ fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 12, background: '#f3f4f6', color: '#6b7280', border: '1px solid #d1d5db' }}>
                      ⭐ Elutasítva
                    </span>
                  )}
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button
                      disabled={excellentSaving || app.excellentApproved === true}
                      onClick={() => void handleExcellentDecision(true)}
                      style={{ fontSize: 11, padding: '3px 10px', borderRadius: 'var(--radius-sm)', border: '1px solid #6ee7b7', background: '#d1fae5', color: '#065f46', fontWeight: 700, cursor: excellentSaving || app.excellentApproved === true ? 'not-allowed' : 'pointer', opacity: app.excellentApproved === true ? 0.55 : 1 }}
                    >
                      ✨ Elfogad
                    </button>
                    <button
                      disabled={excellentSaving || app.excellentApproved === false}
                      onClick={() => void handleExcellentDecision(false)}
                      style={{ fontSize: 11, padding: '3px 10px', borderRadius: 'var(--radius-sm)', border: '1px solid #d1d5db', background: '#f3f4f6', color: '#6b7280', fontWeight: 700, cursor: excellentSaving || app.excellentApproved === false ? 'not-allowed' : 'pointer', opacity: app.excellentApproved === false ? 0.55 : 1 }}
                    >
                      Elutasít
                    </button>
                    {app.excellentApproved !== null && (
                      <button
                        disabled={excellentSaving}
                        onClick={() => void handleExcellentDecision(null)}
                        style={{ fontSize: 11, padding: '3px 10px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: '#fff', color: 'var(--muted-foreground)', cursor: excellentSaving ? 'not-allowed' : 'pointer' }}
                      >
                        ↺
                      </button>
                    )}
                  </div>
                  {excellentError && <span style={{ fontSize: 11, color: '#dc2626' }}>{excellentError}</span>}
                </div>
              )}
              {/* Conflict of interest button */}
              <button
                onClick={() => setConflictModal(true)}
                style={{
                  padding: '6px 14px', fontSize: 12, fontWeight: 600,
                  background: 'transparent', color: '#b45309',
                  border: '1px solid #d97706', borderRadius: 'var(--radius-sm)',
                  cursor: 'pointer', whiteSpace: 'nowrap',
                }}>
                ⚠ Összeférhetetlenség jelzése
              </button>
            </div>
          </div>
        </div>

        {/* Pre-qual result */}
        {app.prequalification && (
          <div style={{ marginBottom: 20, padding: '10px 16px', borderRadius: 'var(--radius-sm)', background: app.prequalification.passed ? 'rgba(78,192,124,.12)' : 'rgba(209,49,46,.08)', border: '1px solid ' + (app.prequalification.passed ? 'rgba(78,192,124,.4)' : 'rgba(209,49,46,.25)'), fontSize: 13 }}>
            <strong>Előminősítő:</strong> {app.prequalification.total_score} / {app.prequalification.max_score} pont
            {' – '}<strong style={{ color: app.prequalification.passed ? '#064935' : 'var(--destructive)' }}>{app.prequalification.passed ? 'Megfelelt' : 'Nem felelt meg'}</strong>
          </div>
        )}

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid var(--border)', marginBottom: 24 }}>
          {(['review', 'uploads', 'deficiency', 'preq', 'ai', 'compliance'] as const).map((tab) => {
            let label = '';
            if (tab === 'review')           label = 'Bírálat';
            else if (tab === 'uploads')     label = 'Mellékletek' + (uploadsLoaded ? ' (' + uploads.length + ')' : '');
            else if (tab === 'ai')          label = 'AI összefoglaló';
            else if (tab === 'preq')        label = 'Előminősítés';
            else if (tab === 'compliance')  label = 'Megfelelőség';
            else label = 'Hiánypótlás' + (pendingDeficiencies.length > 0 ? ' (' + pendingDeficiencies.length + ')' : '');
            return (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                style={{
                  padding: '10px 20px', fontWeight: 700, fontSize: 14,
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: activeTab === tab ? 'var(--primary)' : 'var(--muted-foreground)',
                  borderBottom: '2px solid ' + (activeTab === tab ? 'var(--primary)' : 'transparent'),
                  marginBottom: -2,
                }}
              >
                {label}
              </button>
            );
          })}
        </div>

        {/* Review tab — content sections with inline scoring */}
        {activeTab === 'review' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* General uploads (non-section-specific: logo, photo, recommendation, etc.) */}
            {(() => {
              const general = uploads.filter((u) => u.type !== 'section_file');
              if (!general.length) return null;
              return (
                <div style={{ padding: '12px 18px', background: '#fff', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--secondary-foreground)', marginRight: 4, whiteSpace: 'nowrap' }}>
                    Általános mellékletek
                  </span>
                  {general.map((u) => (
                    u.downloadUrl ? (
                      <a
                        key={u.id}
                        href={u.downloadUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="btn btn-ghost btn-sm"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12 }}
                      >
                        <span>{u.mimeType?.startsWith('image/') ? '🖼' : '📄'}</span> {u.fileName}
                      </a>
                    ) : (
                      <span key={u.id} style={{ fontSize: 12, color: 'var(--muted-foreground)', padding: '5px 8px' }}>
                        📎 {u.fileName}
                      </span>
                    )
                  ))}
                </div>
              );
            })()}

            {finalized && (
              <div style={{ marginBottom: 4 }}>
                <div className="alert alert-success" style={{ marginBottom: unfinalizeError ? 4 : 0 }}>
                  <span>✓</span>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 12 }}>
                    <p style={{ margin: 0 }}>A pontozás véglegesítve.</p>
                    <button
                      className="btn btn-secondary"
                      style={{ fontSize: 12, padding: '4px 12px', whiteSpace: 'nowrap', flexShrink: 0 }}
                      disabled={unfinalizing}
                      onClick={() => void handleUnfinalize()}
                    >
                      {unfinalizing ? 'Visszanyitás...' : 'Visszanyitás'}
                    </button>
                  </div>
                </div>
                {unfinalizeError && (
                  <div className="alert alert-error" style={{ margin: 0 }}>
                    <span>✕</span><div><p>{unfinalizeError}</p></div>
                  </div>
                )}
              </div>
            )}
            {saveError && (
              <div className="alert alert-error" style={{ marginBottom: 4 }}>
                <span>✕</span><div><p>{saveError}</p></div>
              </div>
            )}

            {(() => {
              return Object.entries(app.formData).sort(([a], [b]) => compareSections(a, b)).map(([section, fields]) => {
              const isNumbered = /^\d+\.\d+/.test(section);
              // Match by criterionId, not by position — avoids misalignment when non-numbered criteria
              // (e.g. bonus_photos) sort before numbered ones in sortedCriteria.
              const crit = isNumbered ? (sortedCriteria.find((c) => c.criterionId === section) ?? null) : null;
              const existing     = crit ? scores[crit.criterionId] : null;
              const d            = crit ? (draft[crit.criterionId] ?? { score: '', comment: '' }) : null;
              const isSavingThis = crit ? saving === crit.criterionId : false;
              const isSaved      = !!existing;

              return (
                <div key={section} style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden', boxShadow: 'var(--shadow-sm)' }}>
                  {/* Section content */}
                  <div style={{ padding: '24px 28px' }}>
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--secondary-foreground)' }}>
                        {section}
                      </div>
                      {crit && (
                        <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--primary)', marginTop: 3 }}>{crit.label}</div>
                      )}
                    </div>
                    {(() => {
                      const contentEntries = Object.entries(fields).filter(([, v]) => v !== null && v !== undefined && v !== '');
                      if (!contentEntries.length) {
                        return (
                          <div style={{ fontSize: 13, color: 'var(--muted-foreground)', fontStyle: 'italic', marginBottom: 8 }}>
                            A pályázó nem töltötte ki ezt a szekciót.
                          </div>
                        );
                      }
                      return contentEntries.map(([key, value]) => {
                        const isUrl = typeof value === 'string' && /^https?:\/\//i.test(value);
                        // Human-readable label for known field keys
                        const FIELD_LABELS: Record<string, string> = {
                          video_url:   'Videó link',
                          content:     '',
                          text:        '',
                        };
                        const label = key in FIELD_LABELS ? FIELD_LABELS[key] : key;
                        return (
                          <div key={key} style={{ marginBottom: 10 }}>
                            {label && (
                              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--secondary-foreground)', marginBottom: 3 }}>
                                {label}
                              </div>
                            )}
                            {isUrl ? (
                              <a href={value} target="_blank" rel="noreferrer"
                                style={{ fontSize: 14, color: 'var(--color-link, #0077cc)', wordBreak: 'break-all', lineHeight: 1.6 }}>
                                {value}
                              </a>
                            ) : (
                              <div style={{ fontSize: 14, color: 'var(--primary)', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{value}</div>
                            )}
                          </div>
                        );
                      });
                    })()}
                    {/* Attachments for this section */}
                    {(() => {
                      const sectionFiles = uploads.filter((u) => u.type === 'section_file' && u.sectionId === section);
                      if (!sectionFiles.length) return null;
                      return (
                        <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {sectionFiles.map((u) => (
                            u.downloadUrl ? (
                              <a
                                key={u.id}
                                href={u.downloadUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="btn btn-ghost btn-sm"
                                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}
                              >
                                <span>{u.mimeType?.startsWith('image/') ? '🖼' : '📄'}</span> {u.fileName}
                              </a>
                            ) : (
                              <span key={u.id} style={{ fontSize: 13, color: 'var(--muted-foreground)', padding: '5px 10px' }}>
                                📎 {u.fileName}
                              </span>
                            )
                          ))}
                        </div>
                      );
                    })()}
                  </div>

                  {/* Per-section deficiency note (sent to applicant with deficiency request) */}
                  {canCreateDeficiency && (
                    <div style={{
                      borderTop: '1px solid #f0c040',
                      background: 'rgba(240,192,64,.07)',
                      padding: '12px 28px',
                    }}>
                      <label style={{ display: 'block', fontSize: 11, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: '#7a5a00', marginBottom: 6 }}>
                        Hiánypótlási megjegyzés a pályázónak
                      </label>
                      <textarea spellCheck={true} lang="hu"
                        value={sectionNotes[section] ?? ''}
                        onChange={(e) => setSectionNotes((prev) => ({ ...prev, [section]: e.target.value }))}
                        placeholder="Ha ehhez a kérdéshez hiánypótlás szükséges, ide írja a kérést..."
                        rows={2}
                        style={{ width: '100%', resize: 'vertical', boxSizing: 'border-box', fontSize: 13 }}
                      />
                    </div>
                  )}

                  {/* Inline scoring zone */}
                  {crit && (
                    <div style={{
                      borderTop: '1px solid var(--border)',
                      background: 'rgba(0,119,204,.04)',
                      padding: '16px 28px',
                      opacity: finalized && !isSaved ? 0.55 : 1,
                    }}>
                      {crit.rubricType === 'standard' && crit.subcriteria.length > 0 ? (
                        /* Standard criterion: 4 sub-criterion inputs */
                        <div>
                          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--secondary-foreground)', marginBottom: 8 }}>
                            Szempontok szerinti értékelés
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {crit.subcriteria.map((sub) => {
                              const subCritId  = `${crit.criterionId}.${sub.subcritId}`;
                              const sm         = sub.maxScore;
                              const subDraft   = draft[subCritId] ?? { score: '', comment: '' };
                              const subSaved   = !!scores[subCritId];
                              const subSaving  = saving === subCritId;
                              return (
                                <div key={subCritId} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                  <span style={{ flex: 1, fontSize: 13, color: 'var(--primary)' }}>{sub.label}</span>
                                  <input
                                    type="number" min={0} max={sm}
                                    value={subDraft.score}
                                    disabled={finalized}
                                    onChange={(e) => {
                                      const raw    = e.target.value;
                                      const parsed = parseInt(raw, 10);
                                      const score  = raw === '' ? '' : isNaN(parsed) ? '' : String(Math.max(0, Math.min(parsed, sm)));
                                      setDraft((prev) => ({ ...prev, [subCritId]: { ...prev[subCritId], score } }));
                                      if (score !== '') scheduleSave(subCritId, parseInt(score, 10), sm);
                                    }}
                                    style={{
                                      width: 56, padding: '5px 8px', textAlign: 'center',
                                      borderRadius: 'var(--radius-sm)',
                                      border: '2px solid ' + (subSaved ? '#0077cc' : 'var(--border)'),
                                      fontSize: 14, fontWeight: 700, color: 'var(--primary)',
                                    }}
                                  />
                                  <span style={{ fontSize: 12, color: 'var(--muted-foreground)', minWidth: 36 }}>/ {sm}</span>
                                  {subSaving && <span style={{ fontSize: 10, color: '#7a5a00' }}>…</span>}
                                  {subSaved && !subSaving && <span style={{ fontSize: 10, color: '#1a7a40' }}>✓</span>}
                                </div>
                              );
                            })}
                          </div>
                          {/* Criterion subtotal */}
                          <div style={{ marginTop: 10, fontSize: 12, color: 'var(--secondary-foreground)', fontWeight: 600, textAlign: 'right' }}>
                            Összesen: {crit.subcriteria.reduce((s, sub) => s + (scores[`${crit.criterionId}.${sub.subcritId}`]?.score ?? 0), 0)} / {crit.maxScore}
                            {isSaved && (finalized ? ' – Véglegesített' : '')}
                          </div>
                        </div>
                      ) : (
                        /* Simple criterion: single input + comment */
                        <>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' }}>
                            <input
                              type="number" min={0} max={crit.maxScore}
                              value={d!.score}
                              disabled={finalized}
                              onChange={(e) => {
                                const raw    = e.target.value;
                                const parsed = parseInt(raw, 10);
                                const score  = raw === '' ? '' : isNaN(parsed) ? '' : String(Math.max(0, Math.min(parsed, crit.maxScore)));
                                const newDraft = { ...(draft[crit.criterionId] ?? { comment: '' }), score };
                                setDraft((prev) => ({ ...prev, [crit.criterionId]: newDraft }));
                                if (score !== '') scheduleSave(crit.criterionId, parseInt(score, 10), crit.maxScore, newDraft.comment);
                              }}
                              style={{
                                width: 64, padding: '6px 10px', textAlign: 'center',
                                borderRadius: 'var(--radius-sm)',
                                border: '2px solid ' + (isSaved ? '#0077cc' : 'var(--border)'),
                                fontSize: 15, fontWeight: 700, color: 'var(--primary)',
                              }}
                            />
                            <span style={{ fontSize: 13, color: 'var(--muted-foreground)', whiteSpace: 'nowrap' }}>/ {crit.maxScore}</span>
                            <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999,
                              color: isSavingThis ? '#7a5a00' : '#1a7a40',
                              background: isSavingThis ? 'rgba(240,192,64,.18)' : 'rgba(78,192,124,.12)',
                            }}>
                              {isSavingThis ? 'Mentés...' : isSaved ? (existing!.isFinal ? 'Véglegesített' : 'Elmentve') : ''}
                            </span>
                          </div>
                          <textarea spellCheck lang="hu"
                            value={d!.comment}
                            disabled={finalized}
                            onChange={(e) => {
                              const newDraft = { ...(draft[crit.criterionId] ?? { score: '' }), comment: e.target.value };
                              setDraft((prev) => ({ ...prev, [crit.criterionId]: newDraft }));
                              if (newDraft.score !== '') scheduleSave(crit.criterionId, parseInt(newDraft.score, 10), crit.maxScore, newDraft.comment);
                            }}
                            placeholder="Megjegyzés (nem kötelező)..."
                            rows={2}
                            style={{ width: '100%', resize: 'vertical', boxSizing: 'border-box', marginTop: 12 }}
                          />
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            }); })()}

            {Object.keys(app.formData).length === 0 && (
              <div style={{ color: 'var(--muted-foreground)', fontSize: 14 }}>Nincs kitöltött tartalom.</div>
            )}

            {/* Criteria with no matching formData section (bonus criteria, presentation, etc.) */}
            {sortedCriteria.filter((c) => !(c.criterionId in app.formData)).map((crit) => {
              const existing     = scores[crit.criterionId];
              const d            = draft[crit.criterionId] ?? { score: '', comment: '' };
              const isSavingThis = saving === crit.criterionId;
              const isSaved      = crit.rubricType === 'standard'
                ? crit.subcriteria.some((sub) => !!scores[`${crit.criterionId}.${sub.subcritId}`])
                : !!existing;
              return (
                <div key={crit.criterionId} style={{
                  background: '#fff', border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-lg)', padding: '20px 24px',
                  boxShadow: 'var(--shadow-sm)',
                  opacity: finalized && !isSaved ? 0.5 : 1,
                }}>
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
                      <div>
                        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--secondary-foreground)' }}>{crit.criterionId}</span>
                        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--primary)', marginTop: 2 }}>{crit.label}</div>
                      </div>
                      {crit.rubricType === 'simple' && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                          <input
                            type="number" min={0} max={crit.maxScore}
                            value={d.score}
                            disabled={finalized}
                            onChange={(e) => {
                              const raw    = e.target.value;
                              const parsed = parseInt(raw, 10);
                              const score  = raw === '' ? '' : isNaN(parsed) ? '' : String(Math.max(0, Math.min(parsed, crit.maxScore)));
                              const newDraft = { ...(draft[crit.criterionId] ?? { comment: '' }), score };
                              setDraft((prev) => ({ ...prev, [crit.criterionId]: newDraft }));
                              if (score !== '') scheduleSave(crit.criterionId, parseInt(score, 10), crit.maxScore, newDraft.comment);
                            }}
                            style={{
                              width: 64, padding: '6px 10px', textAlign: 'center',
                              borderRadius: 'var(--radius-sm)',
                              border: '2px solid ' + (isSaved ? 'var(--secondary)' : 'var(--border)'),
                              fontSize: 15, fontWeight: 700, color: 'var(--primary)',
                            }}
                          />
                          <span style={{ fontSize: 13, color: 'var(--muted-foreground)', whiteSpace: 'nowrap' }}>/ {crit.maxScore}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {crit.rubricType === 'standard' && crit.subcriteria.length > 0 ? (
                    /* Standard criterion: 4 sub-criterion inputs */
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--secondary-foreground)', marginBottom: 8 }}>
                        Szempontok szerinti értékelés
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {crit.subcriteria.map((sub) => {
                          const subCritId = `${crit.criterionId}.${sub.subcritId}`;
                          const sm        = sub.maxScore;
                          const subDraft  = draft[subCritId] ?? { score: '', comment: '' };
                          const subSaved  = !!scores[subCritId];
                          const subSaving = saving === subCritId;
                          return (
                            <div key={subCritId} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              <span style={{ flex: 1, fontSize: 13, color: 'var(--primary)' }}>{sub.label}</span>
                              <input
                                type="number" min={0} max={sm}
                                value={subDraft.score}
                                disabled={finalized}
                                onChange={(e) => {
                                  const raw    = e.target.value;
                                  const parsed = parseInt(raw, 10);
                                  const score  = raw === '' ? '' : isNaN(parsed) ? '' : String(Math.max(0, Math.min(parsed, sm)));
                                  setDraft((prev) => ({ ...prev, [subCritId]: { ...prev[subCritId], score } }));
                                  if (score !== '') scheduleSave(subCritId, parseInt(score, 10), sm);
                                }}
                                style={{
                                  width: 56, padding: '5px 8px', textAlign: 'center',
                                  borderRadius: 'var(--radius-sm)',
                                  border: '2px solid ' + (subSaved ? 'var(--secondary)' : 'var(--border)'),
                                  fontSize: 14, fontWeight: 700, color: 'var(--primary)',
                                }}
                              />
                              <span style={{ fontSize: 12, color: 'var(--muted-foreground)', minWidth: 36 }}>/ {sm}</span>
                              {subSaving && <span style={{ fontSize: 10, color: '#7a5a00' }}>…</span>}
                              {subSaved && !subSaving && <span style={{ fontSize: 10, color: '#1a7a40' }}>✓</span>}
                            </div>
                          );
                        })}
                      </div>
                      <div style={{ marginTop: 10, fontSize: 12, color: 'var(--secondary-foreground)', fontWeight: 600, textAlign: 'right' }}>
                        Összesen: {crit.subcriteria.reduce((s, sub) => s + (scores[`${crit.criterionId}.${sub.subcritId}`]?.score ?? 0), 0)} / {crit.maxScore}
                      </div>
                    </div>
                  ) : (
                    /* Simple criterion: comment textarea */
                    <>
                      <textarea spellCheck lang="hu"
                        value={d.comment}
                        disabled={finalized}
                        onChange={(e) => {
                          const newDraft = { ...(draft[crit.criterionId] ?? { score: '' }), comment: e.target.value };
                          setDraft((prev) => ({ ...prev, [crit.criterionId]: newDraft }));
                          if (newDraft.score !== '') scheduleSave(crit.criterionId, parseInt(newDraft.score, 10), crit.maxScore, newDraft.comment);
                        }}
                        placeholder="Megjegyzés (nem kötelező)..."
                        rows={2}
                        style={{ width: '100%', resize: 'vertical', boxSizing: 'border-box', marginBottom: 4 }}
                      />
                      {isSaved && (
                        <div style={{ fontSize: 12, color: isSavingThis ? '#7a5a00' : 'var(--secondary-foreground)' }}>
                          {isSavingThis ? 'Mentés...' : 'Elmentve' + (existing?.isFinal ? ' – Véglegesített' : '')}
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}

            {/* Deficiency send panel — visible when deficiency is allowed */}
            {canCreateDeficiency && (() => {
              const filledNotes = Object.values(sectionNotes).filter((v) => v.trim().length > 0);
              const hasNotes    = filledNotes.length > 0;
              const canSend     = hasNotes || deficiencyDesc.trim().length > 0;
              return (
                <div style={{ marginTop: 12, padding: '20px 24px', background: 'rgba(240,192,64,.07)', border: '2px solid #f0c040', borderRadius: 'var(--radius-lg)' }}>
                  <div style={{ fontWeight: 700, color: '#7a5a00', marginBottom: 4 }}>Hiánypótlási kérelem küldése</div>
                  {hasNotes && (
                    <div style={{ fontSize: 12, color: '#7a5a00', marginBottom: 14 }}>
                      {filledNotes.length} kérdésnél van megjegyzés — ezek a pályázóhoz kerülnek.
                    </div>
                  )}
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--secondary-foreground)', marginBottom: 6 }}>
                      Összefoglaló leírás {hasNotes ? '(nem kötelező)' : <span style={{ color: 'var(--destructive)' }}>*</span>}
                    </label>
                    <textarea spellCheck={true} lang="hu"
                      value={deficiencyDesc}
                      onChange={(e) => setDeficiencyDesc(e.target.value)}
                      placeholder="Írja le összefoglalva, milyen kiegészítést kér a pályázótól..."
                      rows={3}
                      style={{ width: '100%', resize: 'vertical', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--secondary-foreground)', marginBottom: 6 }}>
                      Határidő (nem kötelező)
                    </label>
                    <input
                      type="date"
                      value={deficiencyDeadline}
                      min={new Date().toISOString().slice(0, 10)}
                      onChange={(e) => setDeficiencyDeadline(e.target.value)}
                      style={{ padding: '8px 12px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', fontSize: 14 }}
                    />
                  </div>
                  {deficiencyError && (
                    <div style={{ fontSize: 13, color: 'var(--destructive)', marginBottom: 12 }}>{deficiencyError}</div>
                  )}
                  <button
                    className="btn btn-primary"
                    disabled={deficiencySubmitting || !canSend}
                    onClick={() => void handleCreateDeficiency()}
                  >
                    {deficiencySubmitting ? 'Küldés...' : 'Hiánypótlási kérelem küldése'}
                  </button>
                </div>
              );
            })()}

            {/* Reviewer note */}
            <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '20px 24px', boxShadow: 'var(--shadow-sm)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 600 }}>Saját megjegyzés (csak te látod)</span>
                {noteSaved && <span style={{ fontSize: 12, color: 'var(--muted-foreground)' }}>✓ Mentve</span>}
              </div>
              <textarea
                value={note}
                onChange={(e) => handleNoteChange(e.target.value)}
                placeholder="Ide írhatod a saját megjegyzéseidet ehhez a pályázathoz…"
                rows={4}
                style={{
                  width: '100%', resize: 'vertical', boxSizing: 'border-box',
                  border: '1px solid var(--border)', borderRadius: 'var(--radius)',
                  padding: '8px 10px', fontSize: 13, fontFamily: 'inherit',
                  background: 'var(--background)', color: 'var(--foreground)',
                  outline: 'none',
                }}
              />
            </div>

            {/* Finalize button */}
            {!finalized && scoredCount > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
                {finalizeError && (
                  <div className="alert alert-error" style={{ margin: 0 }}>
                    <span>✕</span><div><p>{finalizeError}</p></div>
                  </div>
                )}
                <div style={{ padding: '20px 24px', background: '#fff', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontWeight: 700, color: 'var(--primary)' }}>Pontozás véglegesítése</div>
                    <div style={{ fontSize: 13, color: 'var(--muted-foreground)', marginTop: 2 }}>
                      {scoredCount} / {criteria.length} kritérium kitöltve – összpontszám: {totalScore} / {totalMax}
                    </div>
                  </div>
                  <button className="btn btn-primary" disabled={finalizing} onClick={() => void handleFinalize()}>
                    {finalizing ? 'Véglegesítés...' : 'Véglegesítés'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Uploads tab */}
        {activeTab === 'uploads' && (
          <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '24px 28px', boxShadow: 'var(--shadow-sm)' }}>
            {uploadsLoaded && uploads.length > 0 && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16, gap: 8 }}>
                {zipError && <span style={{ fontSize: 12, color: 'var(--destructive)', alignSelf: 'center' }}>{zipError}</span>}
                <button
                  onClick={handleDownloadAttachmentsZip}
                  disabled={zipDownloading}
                  style={{ fontSize: 13, padding: '6px 14px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', background: '#fff', cursor: zipDownloading ? 'not-allowed' : 'pointer', color: 'var(--foreground)' }}
                >
                  {zipDownloading ? 'Letöltés...' : '⬇ Összes melléklet letöltése'}
                </button>
              </div>
            )}
            {!uploadsLoaded ? (
              <p style={{ fontSize: 13, color: 'var(--muted-foreground)', margin: 0 }}>Betöltés...</p>
            ) : uploads.length === 0 ? (
              <p style={{ fontSize: 13, color: 'var(--muted-foreground)', margin: 0 }}>Nincs feltöltött melléklet.</p>
            ) : (() => {
                const regularUploads = uploads.filter((u) => u.type !== 'section_file');
                const sectionUploads = uploads.filter((u) => u.type === 'section_file');
                const sectionGroups = sectionUploads.reduce<Record<string, typeof sectionUploads>>((acc, u) => {
                  const key = (u as { sectionId?: string | null }).sectionId ?? 'unknown';
                  (acc[key] ??= []).push(u);
                  return acc;
                }, {});
                const renderTable = (rows: typeof uploads) => (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--border)' }}>
                        <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--secondary-foreground)', fontWeight: 700, fontSize: 11 }}>Típus</th>
                        <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--secondary-foreground)', fontWeight: 700, fontSize: 11 }}>Fájlnév</th>
                        <th style={{ textAlign: 'right', padding: '6px 8px', color: 'var(--secondary-foreground)', fontWeight: 700, fontSize: 11 }}>Méret</th>
                        <th style={{ textAlign: 'right', padding: '6px 8px', color: 'var(--secondary-foreground)', fontWeight: 700, fontSize: 11 }}>Feltöltve</th>
                        <th style={{ padding: '6px 8px' }} />
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((u) => (
                        <tr key={u.id} style={{ borderBottom: '1px solid var(--border)' }}>
                          <td style={{ padding: '8px 8px', color: 'var(--muted-foreground)' }}>{u.type}</td>
                          <td style={{ padding: '8px 8px', color: 'var(--primary)', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.fileName}</td>
                          <td style={{ padding: '8px 8px', textAlign: 'right', color: 'var(--muted-foreground)' }}>
                            {u.fileSize != null ? (u.fileSize / 1024).toFixed(0) + ' KB' : '–'}
                          </td>
                          <td style={{ padding: '8px 8px', textAlign: 'right', color: 'var(--muted-foreground)' }}>
                            {new Date(u.uploadedAt).toLocaleDateString('hu-HU')}
                          </td>
                          <td style={{ padding: '8px 8px', textAlign: 'right' }}>
                            {u.downloadUrl ? (
                              <a href={u.downloadUrl} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm" style={{ fontSize: 12 }}>
                                Letöltés
                              </a>
                            ) : <span style={{ fontSize: 12, color: 'var(--muted-foreground)' }}>–</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                );
                return (
                  <>
                    {regularUploads.length > 0 && renderTable(regularUploads)}
                    {Object.entries(sectionGroups).map(([sid, rows]) => (
                      <div key={sid} style={{ marginTop: 20 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--secondary-foreground)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 8 }}>
                          Szekció mellékletek — {sid}
                        </div>
                        {renderTable(rows)}
                      </div>
                    ))}
                  </>
                );
              })()}
          </div>
        )}

        {/* Deficiency tab — history only; sending is done from the Bírálat tab */}
        {activeTab === 'deficiency' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

            {/* Existing requests */}
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--secondary-foreground)', marginBottom: 12 }}>
                Korábbi kérelmek ({deficiencies.length})
              </div>
              {deficiencies.length === 0 ? (
                <div style={{ fontSize: 13, color: 'var(--muted-foreground)' }}>Még nem volt hiánypótlási kérelem ennél a pályázatnál.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {deficiencies.map((d) => {
                    // Compute diff: fields changed/added since snapshot
                    const diffFields: { section: string; key: string; before: string | null; after: string | null }[] = [];
                    if (d.resolvedAt && d.formSnapshot && app) {
                      for (const [section, fields] of Object.entries(app.formData)) {
                        for (const [key, afterVal] of Object.entries(fields)) {
                          const beforeVal = d.formSnapshot[section]?.[key] ?? null;
                          if ((afterVal ?? '') !== (beforeVal ?? '')) {
                            diffFields.push({ section, key, before: beforeVal, after: afterVal });
                          }
                        }
                      }
                      // Also catch newly added sections
                      for (const [section, fields] of Object.entries(d.formSnapshot)) {
                        for (const [key, beforeVal] of Object.entries(fields)) {
                          const afterVal = app.formData[section]?.[key] ?? null;
                          if (afterVal === null && beforeVal !== null) {
                            if (!diffFields.find((x) => x.section === section && x.key === key)) {
                              diffFields.push({ section, key, before: beforeVal, after: null });
                            }
                          }
                        }
                      }
                    }

                    return (
                    <div
                      key={d.id}
                      style={{
                        background: '#fff',
                        border: '1px solid ' + (d.resolvedAt ? 'var(--border)' : '#f0c040'),
                        borderRadius: 'var(--radius-lg)',
                        padding: '18px 22px',
                        boxShadow: 'var(--shadow-sm)',
                      }}
                    >
                      {/* Header row */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 10 }}>
                        <div style={{ fontSize: 13, color: 'var(--muted-foreground)' }}>
                          {new Date(d.createdAt).toLocaleDateString('hu-HU')} · {d.createdByEmail}
                        </div>
                        <div style={{
                          fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 999,
                          background: d.resolvedAt ? 'rgba(78,192,124,.12)' : 'rgba(240,192,64,.18)',
                          color: d.resolvedAt ? '#1a7a40' : '#7a5a00',
                          flexShrink: 0,
                        }}>
                          {d.resolvedAt ? 'Megválaszolt' : 'Várakozás'}
                        </div>
                      </div>

                      {/* Reviewer description */}
                      <div style={{ fontSize: 14, color: 'var(--primary)', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                        {d.description}
                      </div>
                      {d.deadline && (
                        <div style={{ fontSize: 12, color: 'var(--muted-foreground)', marginTop: 6 }}>
                          Határidő: {new Date(d.deadline).toLocaleDateString('hu-HU')}
                        </div>
                      )}

                      {/* Per-section notes sent with this request */}
                      {d.sectionNotes && Object.keys(d.sectionNotes).length > 0 && (
                        <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 6 }}>
                          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: '#7a5a00', marginBottom: 2 }}>
                            Kérdésenkénti megjegyzések
                          </div>
                          {Object.entries(d.sectionNotes).map(([sectionId, note]) => (
                            <div key={sectionId} style={{ padding: '8px 12px', background: 'rgba(240,192,64,.1)', borderRadius: 'var(--radius-sm)', border: '1px solid rgba(240,192,64,.4)' }}>
                              <div style={{ fontSize: 11, fontWeight: 700, color: '#7a5a00', marginBottom: 3 }}>{sectionId}</div>
                              <div style={{ fontSize: 13, color: 'var(--primary)', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{note}</div>
                              {/* Applicant's response to this section */}
                              {d.sectionResponses?.[sectionId] && (
                                <div style={{ marginTop: 8, padding: '6px 10px', background: 'rgba(78,192,124,.1)', borderRadius: 4, border: '1px solid rgba(78,192,124,.3)' }}>
                                  <div style={{ fontSize: 10, fontWeight: 700, color: '#1a7a40', marginBottom: 2 }}>Pályázó válasza</div>
                                  <div style={{ fontSize: 12, color: 'var(--primary)', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{d.sectionResponses[sectionId]}</div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Resolved section */}
                      {d.resolvedAt && (
                        <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
                          <div style={{ fontSize: 12, color: '#1a7a40', fontWeight: 700, marginBottom: 8 }}>
                            ✓ Megválaszolva: {new Date(d.resolvedAt).toLocaleDateString('hu-HU')}
                          </div>

                          {/* Applicant global response text */}
                          {d.applicantResponse && (
                            <div style={{ marginBottom: 14, padding: '10px 14px', background: 'rgba(78,192,124,.08)', borderRadius: 'var(--radius-sm)', border: '1px solid rgba(78,192,124,.25)' }}>
                              <div style={{ fontSize: 11, fontWeight: 700, color: '#1a7a40', marginBottom: 4 }}>Pályázó általános megjegyzése</div>
                              <div style={{ fontSize: 13, color: 'var(--primary)', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{d.applicantResponse}</div>
                            </div>
                          )}

                          {/* Diff view */}
                          {diffFields.length > 0 ? (
                            <div>
                              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--secondary-foreground)', letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 8 }}>
                                Megváltozott mezők ({diffFields.length})
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                {diffFields.map((f) => (
                                  <div key={f.section + '.' + f.key} style={{ background: 'var(--muted)', borderRadius: 'var(--radius-sm)', padding: '10px 12px', fontSize: 12 }}>
                                    <div style={{ fontWeight: 700, color: 'var(--secondary-foreground)', marginBottom: 6 }}>
                                      {f.section} › {f.key}
                                    </div>
                                    {f.before !== null && (
                                      <div style={{ marginBottom: 4, padding: '4px 8px', background: 'rgba(209,49,46,.08)', borderRadius: 4, borderLeft: '3px solid rgba(209,49,46,.4)', color: '#6b2020', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                                        <span style={{ fontSize: 10, fontWeight: 700, marginRight: 6, opacity: .7 }}>ELŐTTE</span>
                                        {f.before || '(üres)'}
                                      </div>
                                    )}
                                    <div style={{ padding: '4px 8px', background: 'rgba(78,192,124,.1)', borderRadius: 4, borderLeft: '3px solid rgba(78,192,124,.5)', color: '#064935', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                                      <span style={{ fontSize: 10, fontWeight: 700, marginRight: 6, opacity: .7 }}>UTÁNA</span>
                                      {f.after || '(üres)'}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ) : d.formSnapshot ? (
                            <div style={{ fontSize: 12, color: 'var(--muted-foreground)', fontStyle: 'italic' }}>
                              Az adatlapon nem történt változás a kérelem létrehozása óta.
                            </div>
                          ) : null}
                        </div>
                      )}
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Prequalification tab */}
        {activeTab === 'preq' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {preqError && (
              <div className="alert alert-error"><span>✕</span><div><p>{preqError}</p></div></div>
            )}
            {!preqData && !preqError && (
              <p style={{ color: 'var(--muted-foreground)', fontSize: 14 }}>Betöltés...</p>
            )}
            {preqData && (() => {
              const { onlineUrls, renewalBypass, passed, totalScore, maxScore, percentage, answers } = preqData;
              const urlEntries: [string, string | null][] = [
                ['Weboldal',   onlineUrls.websiteUrl],
                ['Facebook',   onlineUrls.facebookUrl],
                ['Instagram',  onlineUrls.instagramUrl],
                ['YouTube',    onlineUrls.youtubeUrl],
                ['LinkedIn',   onlineUrls.linkedinUrl],
                ['Egyéb',      onlineUrls.otherOnlinePresence],
              ];
              const hasUrls = urlEntries.some(([, v]) => v);
              return (
                <>
                  {/* Score summary */}
                  <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '20px 24px', boxShadow: 'var(--shadow-sm)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                      {renewalBypass ? (
                        <span style={{ fontSize: 14, color: 'var(--muted-foreground)' }}>Megújítói bypass — előminősítés átugrva.</span>
                      ) : (
                        <>
                          <span style={{
                            fontWeight: 700, fontSize: 15,
                            color: passed ? 'var(--success, #16a34a)' : 'var(--destructive)',
                          }}>
                            {passed ? '✓ Megfelelt' : '✗ Nem felelt meg'}
                          </span>
                          <span style={{ fontSize: 14, color: 'var(--muted-foreground)' }}>
                            {totalScore} / {maxScore} pont &nbsp;({percentage}%)
                          </span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Online presence URLs */}
                  {hasUrls && (
                    <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '20px 24px', boxShadow: 'var(--shadow-sm)' }}>
                      <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Online jelenlét</h3>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {urlEntries.filter(([, v]) => v).map(([label, url]) => (
                          <div key={label} style={{ display: 'flex', gap: 12, fontSize: 14 }}>
                            <span style={{ color: 'var(--muted-foreground)', minWidth: 80 }}>{label}</span>
                            <a href={url!} target="_blank" rel="noopener noreferrer"
                              style={{ color: 'var(--primary)', wordBreak: 'break-all' }}>
                              {url}
                            </a>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Q&A table */}
                  {answers.length > 0 && (
                    <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '20px 24px', boxShadow: 'var(--shadow-sm)' }}>
                      <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Kérdések és válaszok</h3>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                          <tr style={{ background: 'var(--muted)' }}>
                            <th style={{ textAlign: 'left', padding: '6px 10px', fontWeight: 600 }}>#</th>
                            <th style={{ textAlign: 'left', padding: '6px 10px', fontWeight: 600 }}>Kérdés</th>
                            <th style={{ textAlign: 'left', padding: '6px 10px', fontWeight: 600 }}>Válasz</th>
                            <th style={{ textAlign: 'center', padding: '6px 10px', fontWeight: 600, width: 80 }}>Pont</th>
                          </tr>
                        </thead>
                        <tbody>
                          {answers.map((a) => (
                            <tr key={a.questionNumber} style={{ borderBottom: '1px solid var(--border)', opacity: a.isApplicable ? 1 : 0.5 }}>
                              <td style={{ padding: '7px 10px', color: 'var(--muted-foreground)' }}>{a.questionNumber}</td>
                              <td style={{ padding: '7px 10px' }}>{a.questionText}</td>
                              <td style={{ padding: '7px 10px' }}>
                                {a.isApplicable
                                  ? (a.selectedOptionText ?? '—')
                                  : <span style={{ color: 'var(--muted-foreground)', fontStyle: 'italic' }}>Nem alkalmazható</span>}
                              </td>
                              <td style={{ padding: '7px 10px', textAlign: 'center', fontWeight: 600 }}>
                                {a.isApplicable ? (a.score ?? 0) + ' / ' + a.maxScore : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        )}

        {/* AI summary tab */}
        {activeTab === 'ai' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Action bar */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <button
                onClick={() => void generateAiSummary()}
                disabled={aiGenerating || (aiSummary !== null && ['pending', 'running'].includes(aiSummary.status))}
                style={{
                  padding: '8px 18px', borderRadius: 'var(--radius)', fontWeight: 600,
                  fontSize: 14, cursor: 'pointer', border: 'none',
                  background: 'var(--primary)', color: '#fff',
                  opacity: (aiGenerating || (aiSummary !== null && ['pending', 'running'].includes(aiSummary.status))) ? 0.6 : 1,
                }}
              >
                {aiGenerating ? 'Indítás...' : aiSummary ? 'Újragenerálás' : 'AI elemzés indítása'}
              </button>

              {aiSummary?.isStale && (
                <span style={{
                  fontSize: 12, color: '#b45309',
                  background: '#fef3c7', border: '1px solid #f59e0b',
                  borderRadius: 'var(--radius)', padding: '4px 10px',
                }}>
                  ⚠ A pályázat frissült az összefoglaló generálása óta – érdemes újragenerálni.
                </span>
              )}
            </div>

            {aiError && (
              <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 'var(--radius)', padding: '10px 14px', fontSize: 13, color: '#991b1b' }}>
                {aiError}
              </div>
            )}

            {/* Loading state */}
            {(aiGenerating || (aiSummary && ['pending', 'running'].includes(aiSummary.status))) && (
              <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '32px 28px', textAlign: 'center' }}>
                <p style={{ fontSize: 14, color: 'var(--muted-foreground)', margin: 0 }}>
                  AI elemzés folyamatban… (ez néhány percet vehet igénybe)
                </p>
              </div>
            )}

            {/* Error state */}
            {aiSummary?.status === 'error' && (
              <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 'var(--radius-lg)', padding: '16px 20px' }}>
                <p style={{ fontSize: 13, color: '#991b1b', margin: 0 }}>
                  Az AI elemzés sikertelen volt: {aiSummary.errorMessage ?? 'ismeretlen hiba'}
                </p>
              </div>
            )}

            {/* Loaded — no summary yet */}
            {aiLoaded && !aiSummary && !aiGenerating && (
              <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '32px 28px', textAlign: 'center' }}>
                <p style={{ fontSize: 14, color: 'var(--muted-foreground)', margin: 0 }}>
                  Ehhez a pályázathoz még nincs AI összefoglaló. Kattints az "AI elemzés indítása" gombra.
                </p>
              </div>
            )}

            {/* Done — show summary */}
            {aiSummary?.status === 'done' && aiSummary.data && (() => {
              const d = aiSummary.data as AiSummaryData;
              return (
                <>
                  {/* Overview */}
                  <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '20px 24px', boxShadow: 'var(--shadow-sm)' }}>
                    <h3 style={{ fontSize: 15, fontWeight: 700, margin: '0 0 10px' }}>Összefoglaló</h3>
                    <p style={{ fontSize: 14, lineHeight: 1.6, margin: 0, color: 'var(--foreground)' }}>{d.overview}</p>
                  </div>

                  {/* Strengths / Weaknesses / Risks */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                    {[
                      { label: 'Erősségek', items: d.strengths, color: '#166534', bg: '#f0fdf4', border: '#86efac' },
                      { label: 'Gyengeségek', items: d.weaknesses, color: '#9a3412', bg: '#fff7ed', border: '#fdba74' },
                      { label: 'Kockázatok', items: d.risks, color: '#7c2d12', bg: '#fef2f2', border: '#fca5a5' },
                    ].map(({ label, items, color, bg, border }) => (
                      <div key={label} style={{ background: bg, border: `1px solid ${border}`, borderRadius: 'var(--radius-lg)', padding: '16px 18px' }}>
                        <h4 style={{ fontSize: 13, fontWeight: 700, color, margin: '0 0 8px' }}>{label}</h4>
                        {items.length === 0 ? (
                          <p style={{ fontSize: 12, color: 'var(--muted-foreground)', margin: 0 }}>—</p>
                        ) : (
                          <ul style={{ margin: 0, paddingLeft: 16, fontSize: 13, color: 'var(--foreground)', lineHeight: 1.6 }}>
                            {items.map((item, i) => <li key={i}>{item}</li>)}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Per-section collapsible cards */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {Object.entries(d.sections).sort(([a], [b]) => compareSections(a, b)).map(([sectionId, sec]) => {
                      const isOpen = aiExpanded.has(sectionId);
                      const hasSubs = Object.keys(sec.subcriteria).length > 0;
                      return (
                        <div key={sectionId} style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden', boxShadow: 'var(--shadow-sm)' }}>
                          {/* Section header — click to toggle */}
                          <button
                            onClick={() => toggleAiSection(sectionId)}
                            style={{
                              width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                              padding: '14px 20px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left',
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--muted-foreground)', minWidth: 28 }}>{sectionId}</span>
                              <span style={{ fontSize: 14, fontWeight: 600 }}>{sec.label}</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                              <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--primary)' }}>
                                {sec.suggestedScore} / {sec.maxScore} pt
                              </span>
                              <span style={{ fontSize: 12, color: 'var(--muted-foreground)' }}>{isOpen ? '▲' : '▼'}</span>
                            </div>
                          </button>

                          {/* Expanded content */}
                          {isOpen && (
                            <div style={{ padding: '0 20px 16px', borderTop: '1px solid var(--border)' }}>
                              {sec.note && (
                                <p style={{ fontSize: 13, color: 'var(--foreground)', lineHeight: 1.6, margin: '12px 0 10px' }}>{sec.note}</p>
                              )}

                              {hasSubs && (
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 10 }}>
                                  <thead>
                                    <tr style={{ background: 'var(--muted)' }}>
                                      <th style={{ textAlign: 'left', padding: '6px 10px', fontWeight: 600 }}>Részszempont</th>
                                      <th style={{ textAlign: 'center', padding: '6px 10px', fontWeight: 600, width: 80 }}>Javasolt</th>
                                      <th style={{ textAlign: 'center', padding: '6px 10px', fontWeight: 600, width: 60 }}>Max</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {Object.entries(sec.subcriteria).map(([subId, sub]) => (
                                      <tr key={subId} style={{ borderBottom: '1px solid var(--border)' }}>
                                        <td style={{ padding: '6px 10px' }}>{subId}</td>
                                        <td style={{ padding: '6px 10px', textAlign: 'center', fontWeight: 600, color: 'var(--primary)' }}>{sub.score}</td>
                                        <td style={{ padding: '6px 10px', textAlign: 'center', color: 'var(--muted-foreground)' }}>{sub.max}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}

                              <button
                                onClick={() => void acceptAiScores(sectionId)}
                                disabled={aiAccepting === sectionId}
                                style={{
                                  padding: '6px 16px', borderRadius: 'var(--radius)', fontWeight: 600,
                                  fontSize: 13, cursor: 'pointer', border: '1px solid var(--primary)',
                                  background: 'transparent', color: 'var(--primary)',
                                  opacity: aiAccepting === sectionId ? 0.6 : 1,
                                }}
                              >
                                {aiAccepting === sectionId ? 'Mentés...' : 'Pontok elfogadása'}
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              );
            })()}
          </div>
        )}

        {/* Compliance tab */}
        {activeTab === 'compliance' && (
          <div style={{ padding: '24px 0' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <div style={{ fontSize: 14, fontWeight: 700 }}>Megfelelőségi ellenőrzések</div>
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => void handleComplianceRecheck()}
                disabled={complianceRechecking}
              >
                {complianceRechecking ? 'Ellenőrzés folyamatban…' : '↺ Újra ellenőriz'}
              </button>
            </div>

            {complianceError && (
              <div style={{ color: '#b91c1c', fontSize: 13, marginBottom: 12 }}>{complianceError}</div>
            )}

            {!compliance ? (
              <div style={{ fontSize: 13, color: 'var(--muted-foreground)' }}>
                {complianceRechecking ? 'Ellenőrzés folyamatban…' : 'Még nem futott le ellenőrzés.'}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {(
                  [
                    { key: 'nav',  label: 'NAV Köztartozásmentes',  status: compliance.navStatus,  detail: compliance.navDetail },
                    { key: 'rmk',  label: 'RMK (OMMF)',              status: compliance.rmkStatus,  detail: compliance.rmkDetail },
                    { key: 'nrmk', label: 'NRMK',                    status: compliance.nrmkStatus, detail: compliance.nrmkDetail },
                  ] as const
                ).map(({ key, label, status, detail }) => (
                  <div
                    key={key}
                    style={{
                      display: 'flex', alignItems: 'flex-start', gap: 12,
                      padding: '12px 16px', borderRadius: 'var(--radius)',
                      background: '#fff', border: '1px solid var(--border)',
                    }}
                  >
                    <span style={{
                      fontSize: 15, lineHeight: 1,
                      color: status === 'clean' ? '#16a34a' : status === 'dirty' ? '#dc2626' : '#b45309',
                    }}>
                      {status === 'clean' ? '✓' : status === 'dirty' ? '✗' : '!'}
                    </span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{label}</div>
                      {detail && (
                        <div style={{ fontSize: 12, color: 'var(--muted-foreground)', marginTop: 2 }}>{detail}</div>
                      )}
                    </div>
                    <span style={{
                      fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 99,
                      background: status === 'clean' ? '#dcfce7' : status === 'dirty' ? '#fee2e2' : '#fef3c7',
                      color: status === 'clean' ? '#16a34a' : status === 'dirty' ? '#dc2626' : '#b45309',
                    }}>
                      {status === 'clean' ? 'Rendben' : status === 'dirty' ? 'Kifogás' : 'Hiba'}
                    </span>
                  </div>
                ))}
                <div style={{ fontSize: 11, color: 'var(--muted-foreground)', marginTop: 4 }}>
                  Utolsó ellenőrzés: {new Date(compliance.ranAt).toLocaleString('hu-HU')}
                </div>
              </div>
            )}
          </div>
        )}

      </div>
    </ReviewerLayout>
  );
}
